import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchReminderCalendarService } from "../../services/reminder.services";
import moment from "moment";

// Define the calendar filters interface
export interface ReminderCalendarFilters {
  search: string;
  startDate?: string;
  endDate?: string;
  userId?: string | number | (string | number)[] | undefined;
  reminderType?: string;
  status?: string;
  sortBy?: "created_at" | "remind_on";
  sortOrder?: "asc" | "desc";
}

// Define the state interface
export interface ReminderCalendarState {
  loading: boolean;
  error: string | null;
  events: any[];
  filters: ReminderCalendarFilters;
  initialFilters: ReminderCalendarFilters | null; // Store initial filters for clear functionality
  hasInitialFiltersSaved: boolean;
}

// Initial state
const initialState: ReminderCalendarState = {
  loading: false,
  error: null,
  events: [],
  filters: {
    search: "",
    startDate: undefined,
    endDate: undefined,
    userId: undefined,
    reminderType: undefined,
    status: undefined,
    sortBy: "created_at",
    sortOrder: "desc",
  },
  initialFilters: null,
  hasInitialFiltersSaved: false,
};

// Async thunk for fetching reminder calendar data
export const fetchReminderCalendar = createAsyncThunk(
  "reminderCalendar/fetchReminderCalendar",
  async (filters: ReminderCalendarFilters, { rejectWithValue }) => {
    try {
      console.log("Redux: Fetching reminders with filters:", filters);
      const response = await fetchReminderCalendarService(
        filters.startDate,
        filters.endDate,
        filters.search,
        filters.reminderType,
        filters.status,
        filters.userId,
        filters.sortBy,
        filters.sortOrder
      );
      return { events: response, filters };
    } catch (error: any) {
      console.error("Redux: Error fetching reminders:", error);
      return rejectWithValue(error.message || "Failed to fetch reminders");
    }
  }
);

// Create the slice
const reminderCalendarSlice = createSlice({
  name: "reminderCalendar",
  initialState,
  reducers: {
    // Set filters without triggering fetch
    setFilters: (
      state,
      action: PayloadAction<Partial<ReminderCalendarFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Save initial filters (called on first successful fetch)
    saveInitialFilters: (
      state,
      action: PayloadAction<ReminderCalendarFilters>
    ) => {
      if (!state.hasInitialFiltersSaved) {
        state.initialFilters = { ...action.payload };
        state.hasInitialFiltersSaved = true;
        console.log("Redux: Saved initial filters:", state.initialFilters);
      }
    },

    // Clear all filters and restore initial ones on first clear
    clearFilters: (
      state,
      action: PayloadAction<{ useInitial?: boolean } | undefined>
    ) => {
      const useInitial = action.payload?.useInitial ?? false;

      if (useInitial && state.initialFilters && state.hasInitialFiltersSaved) {
        // Restore initial filters on first clear
        console.log("Redux: Clearing filters - restoring initial filters");
        state.filters = { ...state.initialFilters };
      } else {
        // Default to current month
        console.log("Redux: Clearing filters - using current month");
        const startDate = moment().startOf("month").format("YYYY-MM-DD");
        const endDate = moment().endOf("month").format("YYYY-MM-DD");
        state.filters = {
          search: "",
          startDate,
          endDate,
          userId: undefined,
          reminderType: undefined,
          status: undefined,
          sortBy: "created_at",
          sortOrder: "desc",
        };
      }
    },

    // Reset the "has initial saved" flag (for testing or reset scenarios)
    resetInitialFlag: (state) => {
      state.hasInitialFiltersSaved = false;
      state.initialFilters = null;
    },

    // Set loading state manually
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    // Set error state manually
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Set events manually (for prop-driven scenarios)
    setEvents: (state, action: PayloadAction<any[]>) => {
      state.events = action.payload;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetch reminders pending
      .addCase(fetchReminderCalendar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Handle fetch reminders fulfilled
      .addCase(fetchReminderCalendar.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.events = action.payload.events;
        state.filters = action.payload.filters;

        // Save initial filters if this is the first successful fetch
        if (!state.hasInitialFiltersSaved) {
          state.initialFilters = { ...action.payload.filters };
          state.hasInitialFiltersSaved = true;
          console.log(
            "Redux: Auto-saved initial filters on first fetch:",
            state.initialFilters
          );
        }
      })
      // Handle fetch reminders rejected
      .addCase(fetchReminderCalendar.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch reminders";
      });
  },
});

// Export actions
export const {
  setFilters,
  saveInitialFilters,
  clearFilters,
  resetInitialFlag,
  setLoading,
  setError,
  setEvents,
} = reminderCalendarSlice.actions;

// Export selectors
export const selectReminderCalendar = (state: {
  reminderCalendar: ReminderCalendarState;
}) => state.reminderCalendar;
export const selectReminderCalendarEvents = (state: {
  reminderCalendar: ReminderCalendarState;
}) => state.reminderCalendar.events;
export const selectReminderCalendarFilters = (state: {
  reminderCalendar: ReminderCalendarState;
}) => state.reminderCalendar.filters;
export const selectReminderCalendarLoading = (state: {
  reminderCalendar: ReminderCalendarState;
}) => state.reminderCalendar.loading;
export const selectReminderCalendarError = (state: {
  reminderCalendar: ReminderCalendarState;
}) => state.reminderCalendar.error;
export const selectReminderCalendarInitialFilters = (state: {
  reminderCalendar: ReminderCalendarState;
}) => state.reminderCalendar.initialFilters;

// Export reducer
export default reminderCalendarSlice.reducer;
